/**
 * FilterBuilder — nested AND/OR compound filter editor for saved views.
 *
 * WHY: the auditor flagged that the view engine had no filter-builder
 * UI at all, let alone nested groups or relative dates. This is the
 * real editor: a recursive group component where each group toggles
 * AND/OR, holds leaf conditions (field / comparator / value — with a
 * relative-date "in the last / next N days" option), and can nest
 * sub-groups. All edits are immutable tree rewrites emitted through
 * `onChange`; evaluation lives in the tested pure engine
 * (src/lib/crm/viewConfig.ts) — this component only edits the tree.
 */

import { Plus, Trash2 } from 'lucide-react';
import { C } from '../lib/tokens';
import {
  isFilterNode,
  type FilterLeaf,
  type FilterNode,
} from '../lib/crm/viewConfig';

export interface FilterFieldOption {
  /** Machine field name looked up on the record. */
  field: string;
  /** Human label shown in the select. */
  label: string;
}

interface FilterBuilderProps {
  filter: FilterNode;
  fields: FilterFieldOption[];
  onChange: (next: FilterNode) => void;
  /** Read-only render (e.g. viewer role). */
  disabled?: boolean;
}

const CMP_OPTIONS: Array<{ cmp: FilterLeaf['cmp']; label: string }> = [
  { cmp: 'eq', label: 'is' },
  { cmp: 'neq', label: 'is not' },
  { cmp: 'contains', label: 'contains' },
  { cmp: 'gt', label: '>' },
  { cmp: 'lt', label: '<' },
  { cmp: 'in', label: 'is any of' },
  { cmp: 'relDate', label: 'within days' },
];

/** Immutable child replacement at one index. */
function replaceChild(node: FilterNode, i: number, child: FilterNode | FilterLeaf): FilterNode {
  return { ...node, children: node.children.map((c, j) => (j === i ? child : c)) };
}

/** Immutable child removal at one index. */
function removeChild(node: FilterNode, i: number): FilterNode {
  return { ...node, children: node.children.filter((_, j) => j !== i) };
}

function newLeaf(field: string): FilterLeaf {
  return { field, cmp: 'eq', value: '' };
}

/** One leaf row: field select · comparator select · value editor. */
function LeafRow({
  leaf,
  fields,
  disabled,
  onChange,
  onRemove,
}: {
  leaf: FilterLeaf;
  fields: FilterFieldOption[];
  disabled: boolean;
  onChange: (next: FilterLeaf) => void;
  onRemove: () => void;
}) {
  const isRel = leaf.cmp === 'relDate';
  const relDays = isRel && leaf.value && typeof leaf.value === 'object' ? leaf.value.days ?? 30 : 30;
  const relDir = isRel && leaf.value && typeof leaf.value === 'object' ? leaf.value.dir ?? 'past' : 'past';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <select
        className="glass-input"
        aria-label="Filter field"
        disabled={disabled}
        value={leaf.field}
        onChange={(e) => onChange({ ...leaf, field: e.target.value })}
        style={{ width: 'auto', padding: '5px 8px', fontSize: 12 }}
      >
        {fields.map((f) => (
          <option key={f.field} value={f.field}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        className="glass-input"
        aria-label="Filter comparator"
        disabled={disabled}
        value={leaf.cmp}
        onChange={(e) => {
          const cmp = e.target.value as FilterLeaf['cmp'];
          // Seed a sane value shape when the comparator kind changes.
          const value =
            cmp === 'relDate'
              ? { days: 30, dir: 'past' }
              : cmp === 'in'
                ? []
                : typeof leaf.value === 'string'
                  ? leaf.value
                  : '';
          onChange({ ...leaf, cmp, value });
        }}
        style={{ width: 'auto', padding: '5px 8px', fontSize: 12 }}
      >
        {CMP_OPTIONS.map((o) => (
          <option key={o.cmp} value={o.cmp}>
            {o.label}
          </option>
        ))}
      </select>

      {isRel ? (
        <>
          <select
            className="glass-input"
            aria-label="Relative direction"
            disabled={disabled}
            value={relDir}
            onChange={(e) => onChange({ ...leaf, value: { days: relDays, dir: e.target.value } })}
            style={{ width: 'auto', padding: '5px 8px', fontSize: 12 }}
          >
            <option value="past">in the last</option>
            <option value="future">in the next</option>
          </select>
          <input
            className="glass-input"
            aria-label="Relative days"
            type="number"
            min={1}
            disabled={disabled}
            value={relDays}
            onChange={(e) =>
              onChange({ ...leaf, value: { days: Number(e.target.value) || 0, dir: relDir } })
            }
            style={{ width: 64, padding: '5px 8px', fontSize: 12 }}
          />
          <span className="t-meta">days</span>
        </>
      ) : leaf.cmp === 'in' ? (
        <input
          className="glass-input"
          aria-label="Filter values (comma separated)"
          placeholder="a, b, c"
          disabled={disabled}
          value={Array.isArray(leaf.value) ? leaf.value.join(', ') : ''}
          onChange={(e) =>
            onChange({
              ...leaf,
              value: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s !== ''),
            })
          }
          style={{ width: 160, padding: '5px 8px', fontSize: 12 }}
        />
      ) : (
        <input
          className="glass-input"
          aria-label="Filter value"
          placeholder="value"
          disabled={disabled}
          value={typeof leaf.value === 'string' || typeof leaf.value === 'number' ? String(leaf.value) : ''}
          onChange={(e) => {
            // gt/lt want numbers when the text is numeric; eq/contains keep text.
            const raw = e.target.value;
            const numeric = raw !== '' && !Number.isNaN(Number(raw));
            const wantNumber = (leaf.cmp === 'gt' || leaf.cmp === 'lt') && numeric;
            onChange({ ...leaf, value: wantNumber ? Number(raw) : raw });
          }}
          style={{ width: 140, padding: '5px 8px', fontSize: 12 }}
        />
      )}

      {!disabled && (
        <button
          type="button"
          className="glass-btn"
          aria-label="Remove condition"
          onClick={onRemove}
          style={{ padding: '4px 7px' }}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

/** One group: AND/OR toggle + children (leaves and nested groups). */
function GroupEditor({
  node,
  fields,
  disabled,
  depth,
  onChange,
  onRemove,
}: {
  node: FilterNode;
  fields: FilterFieldOption[];
  disabled: boolean;
  depth: number;
  onChange: (next: FilterNode) => void;
  onRemove?: () => void;
}) {
  const defaultField = fields[0]?.field ?? 'name';
  return (
    <div
      style={{
        display: 'grid',
        gap: 8,
        padding: 10,
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.10)',
        background: depth % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.015)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          role="tablist"
          aria-label="Combine conditions with"
          style={{ display: 'inline-flex', gap: 2, padding: 2, borderRadius: 8, border: '1px solid rgba(255,255,255,0.10)' }}
        >
          {(['and', 'or'] as const).map((op) => (
            <button
              key={op}
              type="button"
              role="tab"
              aria-selected={node.op === op}
              className="glass-btn"
              disabled={disabled}
              onClick={() => onChange({ ...node, op })}
              style={{
                padding: '3px 10px',
                border: 'none',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                background:
                  node.op === op
                    ? 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.14))'
                    : 'transparent',
                color: node.op === op ? '#fff' : C.textMuted,
              }}
            >
              {op}
            </button>
          ))}
        </div>
        <span className="t-meta">
          {node.op === 'and' ? 'all conditions must match' : 'any condition may match'}
        </span>
        <div style={{ flex: 1 }} />
        {!disabled && onRemove && (
          <button
            type="button"
            className="glass-btn"
            aria-label="Remove group"
            onClick={onRemove}
            style={{ padding: '4px 7px' }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      {node.children.length === 0 && (
        <span className="t-meta" style={{ fontStyle: 'italic' }}>
          No conditions — every record passes.
        </span>
      )}

      {node.children.map((child, i) =>
        isFilterNode(child) ? (
          <GroupEditor
            key={i}
            node={child}
            fields={fields}
            disabled={disabled}
            depth={depth + 1}
            onChange={(next) => onChange(replaceChild(node, i, next))}
            onRemove={() => onChange(removeChild(node, i))}
          />
        ) : (
          <LeafRow
            key={i}
            leaf={child}
            fields={fields}
            disabled={disabled}
            onChange={(next) => onChange(replaceChild(node, i, next))}
            onRemove={() => onChange(removeChild(node, i))}
          />
        ),
      )}

      {!disabled && (
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            className="glass-btn"
            onClick={() =>
              onChange({ ...node, children: [...node.children, newLeaf(defaultField)] })
            }
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            <Plus size={12} /> Condition
          </button>
          <button
            type="button"
            className="glass-btn"
            onClick={() =>
              onChange({
                ...node,
                children: [...node.children, { op: 'or', children: [newLeaf(defaultField)] }],
              })
            }
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            <Plus size={12} /> Group
          </button>
        </div>
      )}
    </div>
  );
}

export function FilterBuilder({ filter, fields, onChange, disabled = false }: FilterBuilderProps) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <span className="t-section-title">Filters</span>
      <GroupEditor
        node={filter}
        fields={fields}
        disabled={disabled}
        depth={0}
        onChange={onChange}
      />
    </div>
  );
}
